import React, {useCallback, useEffect, useState} from "react";
import {useStores} from "../../../../stores/useStores";
import {useAsync} from "react-async";
import CreateEditNoticeTemplate from "../../../../components/templates/notices/createEditNoticeTemplate";
import {useTranslation} from "react-i18next";
import {ToastMessageStatus} from "../../../../components/atoms/toastMessage/toastMessage";
import {NoticeStatus} from "../../../../shared/enums/noticeStatus";
import {ICustomModalButton} from "../../../../shared/interfaces/customModalButton";
import {observer} from "mobx-react-lite";
import {useHistory, useParams} from "react-router";
import {RoutePaths} from "../../../../shared/routePaths";
import {runInAction} from "mobx";
import {Navigation} from "../../../../utils/navigation";
import {SaveNoticeDetails} from "../../../../shared/interfaces/saveNoticeDetails";
import {NoticePublishType} from "../../../../shared/enums/noticePublishType";
import {format} from "date-fns";
import {utcToZonedTime} from "date-fns-tz";
import UpdatesQuietHourNotificationModal from "../../../../components/organisms/updatesQuietHourNotificationModal/updatesQuietHourNotificationModal";

const EditNotice: React.FC = () => {
  const {
    domainStore: {noticesDomainStore},
    uiStore: {noticesUiStore, modalStore, toastMessageStore, accountStore}
  } = useStores();

  const {
    editableNotice: {notificationDate, notificationEnabled, status}
  } = noticesDomainStore;
  const {t} = useTranslation(["translation", "notice"]);
  const history = useHistory();

  useEffect(() => {
    runInAction(() => {
      noticesUiStore.isCreatingOrEditing = true;
    });
  }, [noticesUiStore]);

  useEffect(() => {
    noticesDomainStore.getVillages();
  }, [noticesDomainStore, noticesDomainStore.editableNotice]);

  const {id} = useParams<{id: string}>();
  const loadNoticeFn = useCallback(
    () => noticesUiStore.selectEditableNotice(id),
    [id, noticesUiStore]
  );

  const [
    isUpdatesQuietHourNotificationModalOpen,
    setIsUpdatesQuietHourNotificationModalOpen
  ] = useState(false);

  const timeOfVillage = utcToZonedTime(
    noticesDomainStore.editableNotice.selectedPublishType !==
      NoticePublishType.Scheduled
      ? new Date()
      : noticesDomainStore.editableNotice.selectedPublishDate.date!,
    noticesDomainStore.getTimeZone()
  );

  const shouldSeeQuietHoursNotificationConfirmation =
    noticesDomainStore.editableNotice.notificationEnabled.value &&
    (timeOfVillage.getHours() >= 20 || timeOfVillage.getHours() < 8) &&
    !noticesDomainStore.editableNotice.isMultiVillage;

  const loadNotice = useAsync({
    deferFn: loadNoticeFn,
    onReject: (error) => modalStore.openErrorModal(error),
    onResolve: () => {
      //Only need to check once, and must wait for the editableNotice to load
      const isNotDraft =
        noticesDomainStore.editableNotice.status !== NoticeStatus.Draft;
      const invalidNotice =
        (!noticesUiStore.editFromList &&
          noticesDomainStore.editableNotice.id === "") ||
        noticesDomainStore.editableNotice.id === null;
      if (isNotDraft && invalidNotice) {
        toastMessageStore.displayToastMessage(
          t("notice:messages.invalidEditableNotice"),
          ToastMessageStatus.Danger,
          8000
        );
        noticesUiStore.resetIsCreatingOrEditingFlag();
        Navigation.navigateWithSelectedVillageId(history, RoutePaths.Notices);
      }

      if (
        (noticesDomainStore.editableNotice.isMultiVillage &&
          !accountStore.abilities.can("Update", "MultiVillage")) || // Redirect if non-MV user tries to edit
        (noticesDomainStore.editableNotice.isAnnouncement &&
          !accountStore.abilities.can("Update", "Announcement"))
      ) {
        // Redirect if non-Announcement.readWrite user tries to edit
        noticesUiStore.resetIsCreatingOrEditingFlag();
        Navigation.navigateWithSelectedVillageId(
          history,
          RoutePaths.ViewNotice,
          noticesDomainStore.editableNotice.id
        );
      }
    }
  });
  if (id && !noticesDomainStore.editableNotice.id && !loadNotice.isPending) {
    noticesDomainStore.resetEditableNotice();
    loadNotice.run();
  }

  const getSuccessMessage = ({
    title,
    selectedPublishDate,
    selectedPublishType,
    status
  }: SaveNoticeDetails) => {
    title = title.trim() !== "" ? title : t("notice:titles.untitledNotice");
    if (
      selectedPublishType === NoticePublishType.Scheduled &&
      status !== NoticeStatus.Draft
    ) {
      return t("notice:messages.createScheduleSuccess", {
        title,
        dateTime: format(
          utcToZonedTime(
            selectedPublishDate!,
            noticesDomainStore.getTimeZone()
          ),
          "dd/MM/yyyy hh:mma"
        )
      });
    }
    return t(
      status === NoticeStatus.Draft
        ? "notice:messages.editDraftSuccess"
        : "notice:messages.editSuccess",
      {title}
    );
  };

  const updateNoticeInternal = (
    status: NoticeStatus,
    shouldResendNotification: boolean = false
  ): Promise<SaveNoticeDetails> => {
    const isDraftOrScheduled =
      noticesDomainStore.editableNotice.status === NoticeStatus.Draft ||
      noticesDomainStore.editableNotice.status === NoticeStatus.Scheduled;
    runInAction(() => {
      noticesDomainStore.editableNotice.status = status;
    });
    if (shouldResendNotification) {
      noticesDomainStore.editableNotice.notificationDate = new Date();
    }
    return noticesDomainStore.updateNotice(isDraftOrScheduled);
  };

  const updateNotice = useAsync({
    deferFn: (args) => updateNoticeInternal(args[0], args[1]),
    onReject: (error) =>
      toastMessageStore.displayToastMessage(
        t(
          noticesDomainStore.editableNotice.status === NoticeStatus.Draft
            ? "notice:messages.editDraftFailure"
            : "notice:messages.editFailure",
          {
            title: noticesDomainStore.editableNotice.title.text
          }
        ),
        ToastMessageStatus.Danger
      ),
    onResolve: (SaveNoticeDetails: SaveNoticeDetails) => {
      toastMessageStore.displayToastMessage(
        getSuccessMessage(SaveNoticeDetails),
        ToastMessageStatus.Success
      );
      Navigation.navigateWithSelectedVillageId(history, RoutePaths.Notices);
    }
  });

  const publishAction = async () => {
    if (shouldSeeQuietHoursNotificationConfirmation) {
      setIsUpdatesQuietHourNotificationModalOpen(true);
    } else {
      const shouldSeeSendAnotherNotice =
        notificationDate && status === NoticeStatus.Published;

      const customButtons: ICustomModalButton[] = [
        {
          type: "primary",
          onClick: () => {
            updateNotice.run(NoticeStatus.Published);
            modalStore.toggle();
          },
          text: t(
            notificationEnabled.value && shouldSeeSendAnotherNotice
              ? "notice:labels.publishOnly"
              : "actions.publish"
          )
        }
      ];

      if (shouldSeeSendAnotherNotice) {
        customButtons.unshift({
          type: "secondary",
          onClick: () => {
            updateNotice.run(NoticeStatus.Published, true);
            modalStore.toggle();
          },
          text: t("notice:labels.publishAndNotify")
        });
      }

      notificationEnabled.value
        ? modalStore.openModalWithContent({
            content: t(
              `notice:messages.${
                shouldSeeSendAnotherNotice
                  ? "sendAnotherNotification"
                  : "sendNotification"
              }`
            ),
            title: t("notice:titles.sendNotification"),
            secondaryButtonText: t("translation:confirmations.negative"),
            centered: true,
            onSecondaryClick: modalStore.toggle,
            customButtons
          })
        : updateNotice.run(NoticeStatus.Published);
    }
  };

  return (
    <>
      <UpdatesQuietHourNotificationModal
        toggle={() =>
          setIsUpdatesQuietHourNotificationModalOpen(
            !isUpdatesQuietHourNotificationModalOpen
          )
        }
        isOpen={isUpdatesQuietHourNotificationModalOpen}
        onPublishBtnClicked={() => updateNotice.run(NoticeStatus.Published)}
        isSaving={updateNotice.isPending}
      />

      <CreateEditNoticeTemplate
        notice={noticesDomainStore.editableNotice}
        publishAction={publishAction}
        saveDraftAction={async () =>
          noticesDomainStore.editableNotice.selectedPublishDate.date === null
            ? updateNotice.run(NoticeStatus.Draft)
            : modalStore.openModalWithContent({
                content: t("notice:messages.saveDraft"),
                title: t("notice:titles.saveDraft"),
                secondaryButtonText: t("notice:actions.continueEditing"),
                canToggle: false,
                centered: true,
                onSecondaryClick: modalStore.toggle,
                customButtons: [
                  {
                    type: "primary-negative",
                    onClick: () => {
                      updateNotice.run(NoticeStatus.Draft);
                      modalStore.toggle();
                    },
                    text: t("notice:actions.saveDraft")
                  }
                ]
              })
        }
        isSaving={updateNotice.isPending}
        isLoading={noticesUiStore.isEditLoading}
        hasUnsavedChanges={noticesDomainStore.editableNotice.hasChanges}
      />
    </>
  );
};

export default observer(EditNotice);
